import Icons from "@/assets/icons";
import Button from "@/components/Button";
import Input from "@/components/Input";
import { ApiService } from "@/services/ApiService";
import { useFormik } from "formik";
import { useNavigate, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import PermissionsList from "@/components/PermissionsList";
import RegistrarUserSchema from "./Schema/RegisterProfileSchema";
import { useTranslation } from "react-i18next";
import { validateFormikFields } from "@/lib/utils";
import { messageService } from "@/services/MessageService";

const RegisterUser = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const { get, post, patch } = ApiService;
  const navigate = useNavigate();
  const schema = RegistrarUserSchema(t);

  const initialValues = {
    nome: "",
    permissoes: [],
  };

  const [apiData, setApiData] = useState<typeof initialValues>();

  const formikData = useFormik({
    initialValues,
    validate: (values) => validateFormikFields(values, schema),
    onSubmit: async ({ nome, permissoes }) => {
      const body = { nome: nome.toUpperCase(), permissoes: permissoes };

      const request = id
        ? patch(`api/v1/perfil/atualizar/${id}`, body)
        : post("api/v1/perfil/criar", body);

      const { status } = await request;

      if (status === 200) {
        const message = id
          ? {
              title: t("profileForm.messages.update.title"),
              detail: t("profileForm.messages.update.description"),
            }
          : {
              title: t("profileForm.messages.save.title"),
              detail: t("profileForm.messages.save.description"),
            };

        messageService.success(message.title, message.detail, () =>
          navigate("/gestao-perfis")
        );
      }
    },
  });

  useEffect(() => {
    const fetchData = async () => {
      const { data } = await get(`api/v1/perfil/buscarPorId/${id}`);
      setApiData(data);
    };

    if (id) {
      fetchData();
    }
  }, [id]);

  useEffect(() => {
    if (apiData) {
      formikData.setValues({
        nome: apiData.nome ?? "",
        permissoes: apiData.permissoes ?? [],
      });
    }
  }, [apiData]);

  // Estilos padronizados
  const inputsFieldsetStyle =
    "bg-white rounded-lg border p-6 shadow-sm flex flex-col gap-6";
  const titleFieldsetStyle =
    "text-xl font-semibold text-gray-800 border-b pb-4 mb-4";
  const buttonsFieldsetStyle =
    "flex max-md:flex-wrap col-span-full justify-end gap-4";

  return (
    <div className="flex-1 p-8 max-w-7xl mx-auto">
      <div className="mb-8 border-b pb-4">
        <h1 className="text-3xl font-medium text-gray-800">
          {id ? t("profileForm.titleEdit") : t("profileForm.title")}
        </h1>
      </div>

      <form onSubmit={formikData.handleSubmit} className="flex flex-col gap-8">
        
        {/* CARD 1: Dados Básicos */}
        <fieldset className={inputsFieldsetStyle}>
          <h2 className={titleFieldsetStyle}>{t("profileForm.sections.main")}</h2>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
            <div className="md:col-span-6">
              <Input
                id="nome"
                label={t("profileForm.inputs.name.label")}
                error={
                  formikData.errors.nome && formikData.touched.nome
                    ? formikData.errors.nome
                    : ""
                }
                {...formikData.getFieldProps("nome")}
              />
            </div>
          </div>
        </fieldset>

        {/* CARD 2: Permissões */}
        <fieldset className={inputsFieldsetStyle}>
          <h2 className={titleFieldsetStyle}>
            {t("profileForm.subtitles.permissions")}
          </h2>
          <div className="w-full">
            <PermissionsList
              containerClassProps="w-full"
              values={formikData.values.permissoes}
              setValues={(newPerms) =>
                formikData.setFieldValue("permissoes", newPerms)
              }
              error={
                formikData.errors.permissoes && formikData.touched.permissoes
                  ? formikData.errors.permissoes
                  : ""
              }
            />
          </div>
        </fieldset>

        <fieldset className={buttonsFieldsetStyle}>
          <Button
            label={t("profileForm.buttons.clear")}
            color="neutro"
            onClick={() => {
              formikData.resetForm();
            }}
            icon={<Icons.Delete pathStyle="fill-inherit" />}
            type="button"
          />

          <Button
            label={t("profileForm.buttons.cancel")}
            color="neutro"
            onClick={() => navigate("/gestao-perfis")}
            icon={<Icons.Close pathStyle="fill-inherit" />}
            type="button"
          />

          <Button
            label={t("profileForm.buttons.save")}
            color="primario"
            disabled={formikData.isSubmitting}
            icon={<Icons.Save pathStyle="fill-inherit" />}
            type="submit"
          />
        </fieldset>
      </form>
    </div>
  );
};

export default RegisterUser;